import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n, LANGUAGES } from "../lib/i18n";
import { Layout } from "../components/Layout";
import { HardHat, Buildings, Briefcase, ArrowRight, HandHeart } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

const ROLE_OPTIONS = [
  { code: "skilled_individual", icon: HardHat, key: "role_individual", desc: "role_individual_desc", tid: "reg-role-individual" },
  { code: "msme", icon: Buildings, key: "role_msme", desc: "role_msme_desc", tid: "reg-role-msme" },
  { code: "employer", icon: Briefcase, key: "role_employer", desc: "role_employer_desc", tid: "reg-role-employer" },
  { code: "sponsor", icon: HandHeart, key: "role_sponsor", desc: "role_sponsor_desc", tid: "reg-role-sponsor" },
];

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") || "skilled_individual";

  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [country, setCountry] = useState("India");
  const [preferredLanguage, setPreferredLanguage] = useState("en");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({
        name, email: email.trim(), phone, password, role,
        city, state: stateName, country, preferred_language: preferredLanguage,
      });
      toast.success("Account created");
      nav("/onboarding", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_occurred"));
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-block mb-6" aria-label="MYTHIVA home">
          <img src="/mythiva-logo.png" alt="MYTHIVA™" className="h-9 w-auto object-contain" draggable="false" />
        </Link>
        <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>{t("sign_up")}</h1>
        <p className="mt-2 text-[var(--as-text-secondary)]">Join MYTHIVA<span className="align-super text-[0.55em]">™</span> · Associate Skill. {t("choose_role")}</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          {ROLE_OPTIONS.map((r) => {
            const Icon = r.icon; const active = role === r.code;
            return (
              <button key={r.code} type="button" onClick={() => setRole(r.code)}
                className={`as-card p-5 text-left transition-all border-2 ${active ? "!border-[var(--as-accent)]" : "!border-[var(--as-border)]"}`}
                data-testid={r.tid}>
                <Icon size={28} weight="duotone" color={active ? "#00B7A6" : "#080F14"} />
                <div className="font-bold mt-3 text-[var(--as-primary)]">{t(r.key)}</div>
                <div className="text-xs text-[var(--as-text-secondary)] mt-1">{t(r.desc)}</div>
              </button>
            );
          })}
        </div>

        <form onSubmit={submit} className="as-card p-7 mt-8 grid md:grid-cols-2 gap-5" data-testid="register-form">
          <div className="md:col-span-2">
            <label className="as-label">{t("name")}</label>
            <input className="as-input" required value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" />
          </div>
          <div>
            <label className="as-label">{t("email")}</label>
            <input className="as-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reg-email" />
          </div>
          <div>
            <label className="as-label">{t("phone")}</label>
            <input className="as-input" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="reg-phone" />
          </div>
          <div className="md:col-span-2">
            <label className="as-label">{t("password")}</label>
            <input className="as-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reg-password" />
          </div>
          <div>
            <label className="as-label">{t("city")}</label>
            <input className="as-input" value={city} onChange={(e) => setCity(e.target.value)} data-testid="reg-city" />
          </div>
          <div>
            <label className="as-label">{t("state")}</label>
            <input className="as-input" value={stateName} onChange={(e) => setStateName(e.target.value)} data-testid="reg-state" />
          </div>
          <div>
            <label className="as-label">{t("country")}</label>
            <input className="as-input" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="reg-country" />
          </div>
          <div>
            <label className="as-label">{t("language")}</label>
            <select className="as-input" value={preferredLanguage} onChange={(e) => setPreferredLanguage(e.target.value)} data-testid="reg-lang">
              {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-3 pt-2">
            <Link to="/login" className="btn-ghost" data-testid="link-to-login">{t("login")}</Link>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="reg-submit">
              {busy ? "..." : t("continue")} <ArrowRight size={18} weight="bold" />
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
